// src/features/files/hooks/useFileUrl.js
//
// Signed file links. <video>, <embed> and <img> can't send our Authorization
// header, so instead of downloading the whole file with axios and showing a
// blob, we ask the API for a short-lived link and hand it to the element:
//
//   const { url } = useFileUrl(fileId);
//   <video src={url} controls />
//
// The browser then streams the file itself: video starts at once and can seek
// (range requests), PDFs show page 1 before the rest has loaded, and nothing
// big is held in JS memory. Links are cached per file/version until shortly
// before they expire, so re-renders and reopening a file don't cost a request.
import { useEffect, useState } from 'react';
import { filesAPI } from '../services/files';

const API_BASE_URL = process.env.REACT_APP_API_BASE_URL || '';
// Fetch a fresh link when less than this is left, so a video started now can
// still load its remaining ranges.
const MIN_REMAINING_MS = 10 * 60 * 1000;

const cache = new Map(); // `${fileId}:${version}` → { url, expiresAt } | Promise

// One token per file/version; size and download are plain query parameters
// on top of it, so a thumbnail and the full file share one request.
async function fetchBaseUrl(fileId, version) {
  const key = `${fileId}:${version ?? ''}`;
  const cached = cache.get(key);
  if (cached instanceof Promise) return cached;
  if (cached && cached.expiresAt - Date.now() > MIN_REMAINING_MS) return cached.url;

  const request = filesAPI.getAccessUrl(fileId, { version })
    .then((res) => {
      const { url, expires_at: expiresAt } = res.data.data;
      const entry = { url: `${API_BASE_URL}${url}`, expiresAt: new Date(expiresAt).getTime() };
      cache.set(key, entry);
      return entry.url;
    })
    .catch((err) => {
      cache.delete(key);
      throw err;
    });
  cache.set(key, request);
  return request;
}

function withParams(url, { size, download } = {}) {
  const params = new URLSearchParams();
  if (size && size !== 'original') params.append('size', size);
  if (download) params.append('download', 'true');
  const qs = params.toString();
  return qs ? `${url}&${qs}` : url;
}

/**
 * Signed URL for showing a file inline. `fileId` null/undefined → no request.
 * `size` ('32x32' … '512x512') gives an image thumbnail; other files ignore it.
 */
export function useFileUrl(fileId, { version = null, size = null } = {}) {
  const [state, setState] = useState({ url: null, loading: Boolean(fileId), error: null });

  useEffect(() => {
    if (!fileId) {
      setState({ url: null, loading: false, error: null });
      return undefined;
    }
    let active = true;
    setState({ url: null, loading: true, error: null });
    fetchBaseUrl(fileId, version)
      .then((url) => active && setState({ url: withParams(url, { size }), loading: false, error: null }))
      .catch((err) => active && setState({ url: null, loading: false, error: err.message || 'Failed to load file' }));
    return () => { active = false; };
  }, [fileId, version, size]);

  return state;
}

/**
 * Forget cached links for a file, e.g. after uploading a new version. A link
 * for "the current version" keeps its URL, and the browser caches the bytes
 * behind it for an hour, so without a fresh link it would keep showing the old file.
 */
export function invalidateFileUrl(fileId) {
  for (const key of cache.keys()) {
    if (key.startsWith(`${fileId}:`)) cache.delete(key);
  }
}

/** Download a file through a signed link (no blob, the browser streams it to disk). */
export async function downloadFileLink(fileId, { version = null, filename } = {}) {
  const url = withParams(await fetchBaseUrl(fileId, version), { download: true });
  const link = document.createElement('a');
  link.href = url;
  if (filename) link.download = filename;
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
}
